<script lang="ts">
  import { onDestroy, onMount, untrack } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { mdiEmailFast } from "@mdi/js"
  import type { OrganizationRoleResponse } from "@kontextwerk/control-web-client"
  import type { IdentityAdministrationApi } from "../../../../../../../../../../../../../../api/kehrblatt/identity-administration"
  import PermissionsGrid from "../../../../../../../../../../../../_shared/_modules/permissions-grid"
  import {
    INVITE_PERMISSION_SECTIONS,
    permissionStateFromGrants,
  } from "../../../../../../../../../../../../_shared/_logic/permission-sections"
  import type { UserDataStepOutput } from "../../_shared/_logic/user-create-workflow"

  const {
    userData,
    access,
    canViewRoles,
    submitting = false,
    onPrevious,
    onSubmit,
  }: {
    userData: UserDataStepOutput
    /** Role reads of the captured context; null when the context is no longer confirmed. */
    access: IdentityAdministrationApi | null
    /** core.roles view: the role grants may be shown. */
    canViewRoles: boolean
    submitting?: boolean
    onPrevious: () => void
    onSubmit: () => void
  } = $props()

  type RoleLoad =
    | { status: "loading" }
    | { status: "ready"; role: OrganizationRoleResponse }
    | { status: "failed" }
    | { status: "hidden" }

  const local = new AbortController()
  let load = $state<RoleLoad>(
    untrack(() => (canViewRoles && access ? { status: "loading" } : { status: "hidden" })),
  )
  const grid = $derived(
    load.status === "ready" ? permissionStateFromGrants(load.role.grants) : null,
  )

  // The step remounts for another role: one read per mount, abandoned on unmount.
  onMount(() => {
    if (!canViewRoles || !access) return

    access
      .role(userData.role.id, local.signal)
      .then((role) => {
        if (!local.signal.aborted) load = { status: "ready", role }
      })
      .catch(() => {
        if (!local.signal.aborted) load = { status: "failed" }
      })
  })

  onDestroy(() => local.abort())
</script>

<div class="user-create-permissions form light">
  <p class="hint">
    Die Rolle <strong>{userData.role.name}</strong> legt fest, worauf {userData.email} nach dem Annehmen
    der Einladung zugreifen darf.
  </p>

  {#if load.status === "loading"}
    <p class="hint" role="status">Berechtigungen der Rolle werden geladen …</p>
  {:else if load.status === "failed"}
    <p class="hint" role="alert">Die Berechtigungen der Rolle konnten nicht geladen werden.</p>
  {:else if load.status === "hidden"}
    <p class="hint">Die Berechtigungen dieser Rolle können Sie mit Ihren Rechten nicht einsehen.</p>
  {:else if grid}
    <PermissionsGrid
      sections={INVITE_PERMISSION_SECTIONS}
      levels={grid.levels}
      scopes={grid.scopes}
      readonly={true}
      invertColors={true}
      brightInputs={true}
    />
  {/if}

  <p class="hint">
    Die eingeladene Person erhält eine E-Mail mit einem Link zur Einladung. Nach dem Annehmen gehört
    sie mit dieser Rolle zur Organisation.
  </p>

  <div class="step-actions">
    <CTA
      type="ghostButton"
      content="Zurück"
      onClick={onPrevious}
      disabled={submitting}
      darkMode={false}
    />
    <CTA
      type="primaryButton"
      content={submitting ? "Lädt…" : "Einladen"}
      onClick={onSubmit}
      disabled={submitting || !access}
      icon={mdiEmailFast}
      dataTestid="controlAdmin.users.invite.submitButton"
    />
  </div>
</div>

<style lang="less">
  .user-create-permissions {
    display: flex;
    flex-direction: column;
    gap: 1rem;
    padding-top: 0.4rem;
    color: var(--text-100);
  }

  .hint {
    margin: 0;
    line-height: 1.5;
    color: var(--text-100);
    overflow-wrap: anywhere;
  }

  .step-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.8rem;
    flex-wrap: wrap;
  }
</style>
